import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import "./ProductPage.scss";
import msraModulesPage from "../../assets/products/msra-modules-page.png";
import msraModuleProgress from "../../assets/products/msra-module-progress.png";
import msraTimedExamPage from "../../assets/products/msra-timed-exam-page.png";
import msraQuestionsPage from "../../assets/products/msra-questions-page.png";
import msraSjts from "../../assets/products/msra-sjts.png";
import { FaCheckCircle, FaBook, FaStar, FaBrain, FaChartLine, FaBullseye, FaUserMd } from "react-icons/fa";
import { topicIcons } from "./topicIcons";

interface MSRAPageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
  };
}

const MSRAPage = ({ product }: MSRAPageProps) => {
  // MSRA topic groups (merged where appropriate to reduce list height)
  const msraTopics = [
    "Allergy, Immunology & Infection",
    "Cardiovascular",
    "Dermatology",
    "Endocrinology & Metabolic",
    "ENT & Respiratory",
    "Ophthalmology & Eyes",
    "Gastroenterology & General Medicine",
    "Genetics",
    "Haematology & Oncology",
    "Musculoskeletal & Trauma",
    "Neurology",
    "Obstetrics & Gynaecology",
    "Paediatrics",
    "Pharmacology & Therapeutics",
    "Psychiatry",
    "Renal & Urology"
  ];

  return (
    <>
      <NavBar />
      <div id="ProductPage" className="product-page--oq">
        <section className="oq-product-hero" aria-label="MSRA Question Bank">
          <div className="site-container oq-product-hero__inner">
            <div className="oq-product-hero__copy">
              <p className="oq-product-hero__eyebrow">MSRA</p>
              <h1 className="oq-product-hero__title">MSRA Question Bank</h1>
              <p className="oq-product-hero__lead">
                3,008 comprehensive questions, of which 2,380 MCQs across 24 topics + 628 Situational Judgment Tests.
                Questions reviewed by medics. Built to help you succeed in the Multi-Specialty Recruitment Assessment.
              </p>
              <div className="oq-product-hero__pricing">
                <span className="oq-product-hero__price">£{product.price}</span>
                <span className="oq-product-hero__length">/ {product.length}</span>
              </div>
              {!product.comingSoon && (
                <div className="oq-product-hero__signup">
                  <Button
                    text="Buy MSRA Question Bank Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                </div>
              )}
            </div>
            <div className="oq-product-hero__visual">
              <div className="oq-product-hero__frame">
                <img
                  className="oq-product-hero__image"
                  src={msraModulesPage}
                  alt="MSRA question bank modules and topics in OphthalmologyQ"
                  width={3154}
                  height={1788}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="product-sales-content">
          <div className="site-container">
            
            {/* Introduction Section */}
            <section className="sales-section intro-section">
              <p className="lead-text">
                The MSRA is a competitive exam that tests clinical knowledge and professional judgment across multiple medical specialties. Success requires comprehensive preparation across a wide range of topics, with questions that reflect real exam difficulty and format.
              </p>
              <p className="solution-text">
                <strong>OphthalmologyQ provides the solution.</strong>
              </p>
            </section>

            {/* Questions Section */}
            <section className="sales-section questions-section">
              <div className="content-with-image">
                <div className="content-side">
                  <div className="section-header">
                    <FaBook className="section-icon" />
                    <h2>2,380 Clinical Knowledge MCQs</h2>
                  </div>
                  <p className="lead-text">
                    Practise with 2,380 carefully written multiple-choice questions, covering 24 essential topics across all major medical specialties.
                  </p>
                  <div className="topics-list msra-topics-list">
                    <h3>Complete Topic Coverage:</h3>
                    <div className="topics-grid msra-topics-grid">
                      {msraTopics.map((topic, index) => (
                        <div key={index} className="topic-card">
                          <div className="topic-icon">{topicIcons[topic] || <FaBook />}</div>
                          <span className="topic-name">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="tagline-text">
                    Every specialty covered. No wasted time.
                  </p>
                </div>
                <div className="image-side">
                  <img src={msraModulesPage} alt="MSRA Question Bank Modules" className="product-screenshot" />
                </div>
              </div>
            </section>

            {/* SJT Section */}
            <section className="sales-section sjt-section">
              <div className="content-with-image">
                <div className="content-side">
                  <div className="section-header">
                    <FaBrain className="section-icon" />
                    <h2>628 Situational Judgment Tests</h2>
                  </div>
                  <p className="lead-text">
                    Master the SJT component with 628 carefully crafted scenarios that test your professional judgment and decision-making skills.
                  </p>
                  <div className="simple-list">
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Real-world professional scenarios</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Ethical dilemmas and conflict resolution</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Teamwork and communication challenges</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Aligned with MSRA SJT format and scoring</p>
                    </div>
                  </div>
                  <p className="emphasis-text">
                    Develop the professional judgment skills that examiners assess.
                  </p>
                </div>
                <div className="image-side">
                  <img src={msraSjts} alt="MSRA Situational Judgment Tests" className="product-screenshot" />
                </div>
              </div>
            </section>

            {/* Quality Section */}
            <section className="sales-section highlight-section">
              <div className="highlight-header">
                <div className="highlight-badge">
                  <FaUserMd className="star-icon" />
                  <span>Medic Reviewed</span>
                </div>
                <h2>Questions Reviewed by Medics</h2>
              </div>
              <p className="lead-text">
                All questions have been carefully reviewed by medics to ensure:
              </p>
              <div className="highlight-features">
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Clinical Accuracy</h3>
                    <p>Questions reflect real-world clinical scenarios and current best practice</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Exam Alignment</h3>
                    <p>Content and difficulty match the actual MSRA exam format</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Quality Assurance</h3>
                    <p>Rigorous review process ensures high-quality, reliable questions</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Explanations Section */}
            <section className="sales-section explanations-section">
              <div className="content-with-image reverse">
                <div className="image-side">
                  <img src={msraQuestionsPage} alt="MSRA Question with Explanation" className="product-screenshot" />
                </div>
                <div className="content-side">
                  <div className="section-header">
                    <FaBrain className="section-icon" />
                    <h2>Learn Faster with Clear Explanations</h2>
                  </div>
                  <p className="lead-text">
                    Every question comes with detailed explanations that help you truly understand the material. You'll see:
                  </p>
                  <div className="simple-list">
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>A clear explanation of the correct answer</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Reasons why the other options are incorrect</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Clinical teaching points to reinforce learning</p>
                    </div>
                  </div>
                  <p className="emphasis-text">
                    You don't just practise — you understand.
                  </p>
                </div>
              </div>
            </section>

            {/* Analytics Section */}
            <section className="sales-section analytics-section">
              <div className="content-with-image">
                <div className="content-side">
                  <div className="section-header">
                    <FaChartLine className="section-icon" />
                    <h2>Track Progress. Target Weaknesses. Improve Faster.</h2>
                  </div>
                  <p className="lead-text">
                    Our built-in analytics help you monitor performance by topic, identify weak areas instantly, and focus revision where it matters most.
                  </p>
                  <p className="tagline-text">
                    Study smarter — not longer.
                  </p>
                </div>
                <div className="image-side">
                  <img src={msraModuleProgress} alt="MSRA Module Progress Analytics" className="product-screenshot" />
                </div>
              </div>
            </section>

            {/* Exam Success Section */}
            <section className="sales-section exam-success-section">
              <div className="content-with-image reverse">
                <div className="image-side">
                  <img src={msraTimedExamPage} alt="MSRA Timed Exam Practice" className="product-screenshot" />
                </div>
                <div className="content-side">
                  <div className="section-header">
                    <FaBullseye className="section-icon" />
                    <h2>Built for Exam Day Success</h2>
                  </div>
                  <p className="lead-text">
                    Everything is designed to prepare you for how the exam actually feels — from exam-style MCQs at realistic difficulty, to time-pressured practice that builds confidence under exam conditions.
                  </p>
                  <p className="emphasis-text">
                    Walk into your exam with confidence.
                  </p>
                </div>
              </div>
            </section>

            {/* Pricing Section */}
            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>MSRA Question Bank</h3>
                <div className="pricing-amount">
                  <span className="price-large">£{product.price}</span>
                  <span className="price-period">— 6 months full access</span>
                </div>
                <div className="pricing-includes">
                  <h4>Includes:</h4>
                  <ul>
                    <li><Checkmark /> 3,008 total questions</li>
                    <li><Checkmark /> 2,380 clinical knowledge MCQs</li>
                    <li><Checkmark /> 628 Situational Judgment Tests</li>
                    <li><Checkmark /> 24 topics across all specialties</li>
                    <li><Checkmark /> Medic-reviewed questions</li>
                    <li><Checkmark /> Detailed explanations</li>
                    <li><Checkmark /> analytics</li>
                  </ul>
                </div>
                <p className="pricing-note">
                  No hidden fees. No unnecessary extras.
                </p>
                <div className="pricing-cta">
                  <Button
                    text="Get Started Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MSRAPage;

